import React from 'react';
import { FormattedMessage, FormattedDate } from '../../util/reactIntl';
import { LINE_ITEM_NIGHT, DATE_TYPE_DATE, LINE_ITEM_HOUR, propTypes } from '../../util/types';
import { addTime, subtractTime } from '../../util/dates';
import Decimal from 'decimal.js';

import css from './OrderBreakdown.module.css';

export const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
          weekday: 'long',
        }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingStart" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>

        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingEnd" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
        </div>
      </div>
    </>
  );
};

const LineItemBookingPeriod = props => {
  const { booking, code, dateType, timeZone, lineItems, guestCountOverride, extraHours } = props;

  if (!booking) {
    return null;
  }
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const isNightly = code === LINE_ITEM_NIGHT;
  const isHour = code === LINE_ITEM_HOUR;
  const endDayBase =
    isNightly || isHour ? localEndDateRaw : subtractTime(localEndDateRaw, 1, 'days');
  // Reflect hour-extension upgrades in the displayed end time.
  const endDay =
    isHour && extraHours > 0 ? addTime(endDayBase, extraHours, 'hours', timeZone) : endDayBase;

  const guestCountMinimum = lineItems.filter(
    item => item.code == 'line-item/guest-count-minimum'
  )[0]?.quantity;
  const guestCountMaximum = lineItems.filter(
    item => item.code == 'line-item/guest-count-maximum'
  )[0]?.quantity;
  const guestCountUpto = lineItems.filter(item => item?.code == 'line-item/guest-count-upto')[0]
    ?.quantity;
  const guestCountMoreThan = lineItems.filter(
    item => item?.code == 'line-item/guest-count-more-than'
  )[0]?.quantity;
  // A group-expansion upgrade overrides the booked guest-count tier.
  const guestMinimum = guestCountOverride
    ? guestCountOverride.min
    : guestCountMinimum
    ? new Decimal(guestCountMinimum).toNumber()
    : 0;
  const guestMaximum = guestCountOverride
    ? guestCountOverride.max
    : guestCountMaximum
    ? new Decimal(guestCountMaximum).toNumber()
    : 0;
  const guestCountUptoNum = guestCountUpto ? new Decimal(guestCountUpto).toNumber() : 0;
  const guestCountMoreThanNum = guestCountMoreThan ? new Decimal(guestCountMoreThan).toNumber() : 0;
  const hasGuestRange = guestCountOverride ? true : guestCountMaximum && guestCountMinimum;

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={localStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
        />
      </div>
      {hasGuestRange ? (
        <div className={css.guestNumber}>
          {guestMinimum === 51 && guestMaximum >= 999 ? (
            <FormattedMessage id="ListingReservationBreakdown.guests51Plus" />
          ) : guestMinimum != guestMaximum ? (
            `Entre ${guestMinimum} y ${guestMaximum} Personas`
          ) : (
            `Entre ${guestMinimum}+ Personas`
          )}
        </div>
      ) : (
        <div className={css.guestNumber}>
          {guestCountUptoNum
            ? `Entre hasta ${guestCountUptoNum} Personas`
            : guestCountMoreThanNum
            ? `Entre ${guestCountMoreThanNum}+ Personas`
            : null}
        </div>
      )}
      <hr className={css.totalDivider} />
    </>
  );
};
LineItemBookingPeriod.defaultProps = {
  booking: null,
  dateType: null,
  guestCountOverride: null,
  extraHours: 0,
};

LineItemBookingPeriod.propTypes = {
  booking: propTypes.booking,
  dateType: propTypes.dateType,
};

export default LineItemBookingPeriod;
